import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    NOVA_INSTR_GORU_HOY,
    buildNovaHoyDump,
    openNovaPopup,
    openPopupUrl,
} from '../informeAvance/novaInformeDump';

/** Imagen del avatar del asistente; si no existe, se cae al emoji (Punto 2). */
const AVATAR_URL_DEFAULT = `${process.env.PUBLIC_URL || ''}/img/goru-avatar.png`;

/**
 * Asistente Goru — contexto efímero vía un popup externo configurable (sin API, sin guardar informe).
 * El botón principal abre el popup configurado por el administrador y copia instrucción + volcado.
 *
 * @param {string} [popupUrl]     URL del popup configurado en /admin (Punto 15). Si viene vacío, usa NOVA.
 * @param {string} [popupNombre]  Nombre visible del asistente (Punto 15).
 * @param {string} [avatarUrl]    Ruta de la imagen del avatar (Punto 2). Fallback a emoji.
 */
function GoruQueHacemosHoyPanel(props) {
    const {
        proyectoId,
        usuario,
        projectDetail,
        resumenEjecucion,
        resumenDesempeno,
        estadisticas,
        logs,
        riesgosList,
        listaSolicitudes,
        listaEncuestas,
        alcanceEntregables,
        tiempoFechasCriticas,
        costoEntregable,
        calidadMetricas,
        todo,
        totalesAprobados,
        presupuesto,
        ganttSummary,
        leccionesAprendidas,
        compact = false,
        popupUrl,
        popupNombre,
        avatarUrl,
    } = props;

    const [avatarError, setAvatarError] = useState(false);

    const nombreAsistente = (popupNombre || '').trim() || 'NOVA';
    const urlAsistente = (popupUrl || '').trim();
    const usarNova = !urlAsistente;

    const avatarSrc = avatarUrl || AVATAR_URL_DEFAULT;

    const dumpProps = {
        proyectoId,
        usuario,
        projectDetail,
        resumenEjecucion,
        resumenDesempeno,
        estadisticas,
        logs,
        riesgosList,
        listaSolicitudes,
        listaEncuestas,
        alcanceEntregables,
        tiempoFechasCriticas,
        costoEntregable,
        calidadMetricas,
        todo,
        totalesAprobados,
        presupuesto,
        ganttSummary,
        leccionesAprendidas,
    };

    const instruccion = useMemo(
        () => NOVA_INSTR_GORU_HOY.replace(/NOVA/g, nombreAsistente),
        [nombreAsistente],
    );

    const copiar = useCallback(async (texto) => {
        try {
            await navigator.clipboard.writeText(texto);
            const truncado = texto.includes('[--- GORU: volcado truncado');
            toast.success(
                truncado
                    ? 'Copiado (volcado truncado al final). Pegá en el asistente.'
                    : `Copiado. Pegá en la ventana emergente de ${nombreAsistente} (Ctrl+V).`,
            );
        } catch {
            toast.warn('No se pudo copiar al portapapeles.');
        }
    }, [nombreAsistente]);

    const handleAbrirAsistente = () => {
        const win = usarNova ? openNovaPopup() : openPopupUrl(urlAsistente);
        if (!win) {
            toast.warn('El navegador bloqueó la ventana emergente. Permití ventanas para este sitio y probá de nuevo.');
        }
        copiar(instruccion + buildNovaHoyDump(dumpProps));
    };

    return (
        <>
            <Card
                className={`border-primary border-opacity-25 shadow-sm ${compact ? '' : 'mb-4'}`}
                style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #fff 100%)' }}
            >
                <Card.Body className={compact ? 'py-3' : 'py-4'}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-2">
                            {avatarError ? (
                                <span className="fs-3" role="img" aria-hidden>
                                    🤖
                                </span>
                            ) : (
                                <img
                                    src={avatarSrc}
                                    alt=""
                                    width={36}
                                    height={36}
                                    style={{ objectFit: 'cover', borderRadius: '50%' }}
                                    onError={() => setAvatarError(true)}
                                />
                            )}
                            <div>
                                <strong className="d-block text-primary">Goru — ¿Qué hacemos hoy?</strong>
                                <small className="text-muted">
                                    Prioridades del día con {nombreAsistente}; no guarda informe (solo contexto copiado).
                                </small>
                            </div>
                        </div>
                        <Button variant="primary" type="button" className="px-4" onClick={handleAbrirAsistente}>
                            Abrir asistente
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Modal heredado (ya no enlazado a la UI); se conserva el componente por si se reutiliza. */}
            <Modal show={false} onHide={() => { }} aria-hidden="true">
                <Modal.Header closeButton>
                    <Modal.Title>Goru — panel heredado</Modal.Title>
                </Modal.Header>
                <Modal.Body className="small text-muted">
                    El flujo actual usa solo el botón «Abrir asistente»: abre el asistente configurado en una ventana
                    emergente y copia el contexto al portapapeles.
                </Modal.Body>
            </Modal>
        </>
    );
}

export default GoruQueHacemosHoyPanel;
